import { useContext, type ReactNode } from 'react'
import {
  ModalOverlay as AriaModalOverlay,
  OverlayTriggerStateContext,
} from 'react-aria-components'
import styles from './ModalOverlay.module.css'
import { m, type Variant, type Variants } from 'motion/react'
import { useProvidePageBottomOffset } from '@/contexts/page-bottom-offset-context'
import {
  AnimationStatePropsProvider,
  useModalAnimationStateControls,
  type ModalAnimationStateProps,
} from '@/contexts/animation-state-context'

export interface ModalOverlayProps {
  children?: ReactNode | ((props: ModalAnimationStateProps) => ReactNode)
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
  isDismissable?: boolean
  isKeyboardDismissDisabled?: boolean
}

// @ts-ignore
const MotionAriaModalOverlay = m.create(AriaModalOverlay)

/**
 * Variant names used for animation propagation in child components.
 */
export const ModalOverlayVariantNames = {
  VISIBLE: 'visible',
  HIDDEN: 'hidden',
} as const

const overlayVariants: Variants = {
  [ModalOverlayVariantNames.VISIBLE]: {
    backgroundColor: `color-mix(in srgb, var(--surface-overlay) 36%, transparent)`,
  } as Variant,
  [ModalOverlayVariantNames.HIDDEN]: {
    backgroundColor: `color-mix(in srgb, var(--surface-overlay) 0%, transparent)`,
  } as Variant,
}

/**
 * The ModalOverlay
 */
export function ModalOverlay({
  isOpen,
  onOpenChange,
  children,
  isDismissable,
  isKeyboardDismissDisabled,
}: ModalOverlayProps) {
  const overlayContext = useContext(OverlayTriggerStateContext)
  isOpen = isOpen ?? overlayContext?.isOpen ?? false
  onOpenChange = onOpenChange ?? overlayContext?.setOpen

  // When showing an overlay, reset the page bottom offset.
  useProvidePageBottomOffset(isOpen ? 0 : null, 1)

  const animationStateProps = useModalAnimationStateControls({
    isOpen: !!isOpen,
    visibleVariant: ModalOverlayVariantNames.VISIBLE,
    hiddenVariant: ModalOverlayVariantNames.HIDDEN,
  })

  return (
    <AnimationStatePropsProvider props={animationStateProps}>
      <MotionAriaModalOverlay
        isOpen={isOpen}
        isDismissable={isDismissable}
        isKeyboardDismissDisabled={isKeyboardDismissDisabled}
        onOpenChange={onOpenChange}
        className={styles.root}
        {...animationStateProps}
      >
        <m.div variants={overlayVariants} className={styles.underlay} />
        {typeof children === 'function'
          ? children(animationStateProps)
          : children}
      </MotionAriaModalOverlay>
    </AnimationStatePropsProvider>
  )
}
